import { useState, useEffect, useCallback } from "react";
import {
  getFirestore,
  collection,
  query,
  where,
  getCountFromServer,
} from "firebase/firestore";

/**
 * Count processed shared videos with a Firestore aggregation (1 billable
 * aggregation read) instead of snapshotting every processed document.
 *
 * Uses the modular SDK — compat Query has no `.count()`.
 *
 * @param {string|null} organizationId
 * @param {{ enabled?: boolean }} [options]
 */
const useProcessedVideosCount = (organizationId, options = {}) => {
  const { enabled = true } = options;
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCount = useCallback(async () => {
    if (!enabled || !organizationId) {
      setCount(0);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const db = getFirestore();
      const countQuery = query(
        collection(db, "organizations", organizationId, "sharedVideos"),
        where("processed", "==", true),
      );
      const snapshot = await getCountFromServer(countQuery);
      setCount(snapshot.data().count || 0);
    } catch (err) {
      console.error("Error fetching processed videos count:", err);
      setError(err.message);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [organizationId, enabled]);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  return { count, loading, error, refetch: fetchCount };
};

/**
 * Static version that fetches count once (same aggregation path).
 */
export const useProcessedVideosCountStatic = (organizationId) => {
  return useProcessedVideosCount(organizationId, { enabled: !!organizationId });
};

export default useProcessedVideosCount;
